import { useEffect, useState } from "react";
import { Button, Form, InputGroup, Modal, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { FiCopy, FiPlus } from "react-icons/fi";
import { createApiToken, getApiTokens } from "../context/functions/AdminFunction";
import "../styles/tenants.css";

const DEFAULT_FORM = {
  name: "",
  status: "active",
};

const EMPTY_VALUE = "-";
const TOKEN_STATUSES = {
  ACTIVE: "active",
  INACTIVE: "inactive",
};

const ApiTokenManagement = () => {
  const [tokenData, setTokenData] = useState(DEFAULT_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingTokens, setIsLoadingTokens] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [createdToken, setCreatedToken] = useState(null);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [apiTokens, setApiTokens] = useState([]);

  const normalizeTokenList = (response) => {
    const list =
      response?.data?.modular_keys ||
      response?.data?.keys ||
      response?.data?.tokens ||
      response?.data?.apiKeys ||
      response?.modular_keys ||
      response?.data ||
      response?.keys ||
      response?.tokens ||
      response?.apiKeys ||
      response;

    return Array.isArray(list) ? list : [];
  };

  const getTokenName = (token) => {
    return token.name || token.keyName || token.title || EMPTY_VALUE;
  };

  const getTokenStatus = (token) => {
    return token.status || EMPTY_VALUE;
  };

  const formatTokenDate = (date) => {
    if (!date) return EMPTY_VALUE;

    const parsedDate = new Date(date);
    if (Number.isNaN(parsedDate.getTime())) {
      return EMPTY_VALUE;
    }

    return parsedDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const normalizeCreatedToken = (response, fallback) => {
    const token = response?.data || response?.key || response?.token || response;
    if (token && typeof token === "object") {
      return {
        ...token,
        name: token.name || fallback.name,
        status: token.status || fallback.status,
      };
    }

    return {
      name: fallback.name,
      status: fallback.status,
      token,
    };
  };

  const getCreatedCredentials = (response) => {
    const data = response?.data || response?.success_res || response || {};

    return {
      clientId:
        data.client_id ||
        data.clientId ||
        data.credentials?.client_id ||
        data.credentials?.clientId ||
        "",
      clientSecret:
        data.client_secret ||
        data.clientSecret ||
        data.credentials?.client_secret ||
        data.credentials?.clientSecret ||
        "",
    };
  };

  const fetchApiTokens = async () => {
    setIsLoadingTokens(true);

    try {
      const response = await getApiTokens();
      setApiTokens(normalizeTokenList(response));
    } catch (error) {
      setApiTokens([]);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to load API tokens"
      );
    } finally {
      setIsLoadingTokens(false);
    }
  };

  useEffect(() => {
    fetchApiTokens();
  }, []);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setTokenData((data) => ({ ...data, [name]: value }));
  };

  const handleCloseCreateModal = () => {
    if (isSubmitting) return;
    setShowCreateModal(false);
    setTokenData(DEFAULT_FORM);
  };

  const handleCreateToken = async (event) => {
    event.preventDefault();

    const name = tokenData.name.trim();
    if (!name) {
      toast.error("Token name is required");
      return;
    }

    setIsSubmitting(true);
    setCreatedToken(null);

    try {
      const payload = {
        name,
        status:
          tokenData.status === TOKEN_STATUSES.INACTIVE
            ? TOKEN_STATUSES.INACTIVE
            : TOKEN_STATUSES.ACTIVE,
      };
      const response = await createApiToken(payload);
      const newToken = normalizeCreatedToken(response, payload);
      const credentials = getCreatedCredentials(response);

      setCreatedToken(newToken);
      setCreatedCredentials(credentials);
      setApiTokens((tokens) => [newToken, ...tokens]);
      setTokenData(DEFAULT_FORM);
      setShowCreateModal(false);
      setShowCredentialsModal(Boolean(credentials.clientId || credentials.clientSecret));
      toast.success(response?.message || "API token created successfully");
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to create API token"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTokenValue = () => {
    if (!createdToken) return "";
    if (typeof createdToken === "string") return createdToken;
    return (
      createdToken.key ||
      createdToken.token ||
      createdToken.apiKey ||
      createdToken.api_key ||
      ""
    );
  };

  const handleCopyToken = async () => {
    const tokenValue = getTokenValue();
    if (!tokenValue) return;

    await handleCopyValue(tokenValue, "Token copied");
  };

  const handleCopyValue = async (value, successMessage = "Copied") => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  const tokenValue = getTokenValue();

  return (
    <div className="api-token-page">
      <div className="header_table d-flex justify-content-between align-items-center">
        <h1>API Token Management</h1>
      </div>

      <div className="api-token-actions">
        <button
          className="api-token-primary-btn"
          type="button"
          onClick={() => setShowCreateModal(true)}
        >
          <FiPlus size={18} />
          <span>Create Token</span>
        </button>
      </div>

      <div className="api-token-layout tenants-container">

        <div className="api-token-list-head d-flex justify-content-between align-items-center">
          <div>
            <h4
              className="fw-800 mb-1"
              style={{
                color: "var(--tenants-color-text-primary)",
                fontSize: "22px",
              }}
            >
              API List
            </h4>
          </div>
          {isLoadingTokens && <Spinner size="sm" />}
        </div>

        <div className="tenants-card">
          <div className="table-responsive">
            <Table responsive hover className="tenants-table mb-0">
              <thead>
                <tr>
                  <th>NAME</th>
                  <th>STATUS</th>
                  <th>CREATED ON</th>
                </tr>
              </thead>
              <tbody>
                {isLoadingTokens ? (
                  <tr>
                    <td colSpan={7}>Loading...</td>
                  </tr>
                ) : apiTokens.length === 0 ? (
                  <tr>
                    <td colSpan={7}>No API tokens found</td>
                  </tr>
                ) : (
                  apiTokens.map((token, index) => (
                    <tr key={token._id || token.id || token.name || index}>
                      <td>
                        <div className="tenant-name-stack">
                          <span className="tenant-name">
                            {getTokenName(token)}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span
                          className={
                            getTokenStatus(token) === "active"
                              ? "tenant-status-active"
                              : "tenant-status-inactive"
                          }
                        >
                          {getTokenStatus(token)}
                        </span>
                      </td>
                      <td className="fw-500">{formatTokenDate(token.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </div>

      <Modal
        show={showCreateModal}
        onHide={handleCloseCreateModal}
        centered
        className="api-token-modal"
      >
        <Modal.Header closeButton className="api-token-modal-header">
          <Modal.Title>Create Token</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleCreateToken}>
          <Modal.Body className="api-token-modal-body">
            <label htmlFor="api-token-name">
              Name
              <InputGroup className="input_table_header">
                <Form.Control
                  id="api-token-name"
                  placeholder="Enter token name"
                  aria-label="Token name"
                  name="name"
                  type="text"
                  value={tokenData.name}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
              </InputGroup>
            </label>

            <label htmlFor="api-token-status">
              Status
              <Form.Select
                id="api-token-status"
                aria-label="Select token status"
                name="status"
                value={tokenData.status}
                onChange={handleInputChange}
                disabled={isSubmitting}
              >
                <option value={TOKEN_STATUSES.ACTIVE}>Active</option>
                <option value={TOKEN_STATUSES.INACTIVE}>Inactive</option>
              </Form.Select>
            </label>
          </Modal.Body>
          <Modal.Footer className="api-token-modal-footer">
            <Button
              className="api-token-secondary-btn"
              type="button"
              onClick={handleCloseCreateModal}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button className="api-token-primary-btn" type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner size="sm" />
                  Creating
                </>
              ) : (
                <>
                  <FiPlus size={18} />
                  Create Token
                </>
              )}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal
        show={showCredentialsModal}
        onHide={() => {}}
        backdrop="static"
        keyboard={false}
        centered
        className="api-token-modal api-token-credentials-modal"
      >
        <Modal.Header className="api-token-modal-header">
          <Modal.Title>API Credentials</Modal.Title>
        </Modal.Header>
        <Modal.Body className="api-token-credentials-body">
          <div className="api-token-credential-row">
            <div className="api-token-credential-copy">
              <span>Client ID</span>
              <code>{createdCredentials?.clientId || EMPTY_VALUE}</code>
            </div>
            <button
              type="button"
              onClick={() =>
                handleCopyValue(createdCredentials?.clientId, "Client ID copied")
              }
              aria-label="Copy client ID"
              disabled={!createdCredentials?.clientId}
            >
              <FiCopy size={18} />
            </button>
          </div>

          <div className="api-token-credential-row">
            <div className="api-token-credential-copy">
              <span>Client Secret</span>
              <code>{createdCredentials?.clientSecret || EMPTY_VALUE}</code>
            </div>
            <button
              type="button"
              onClick={() =>
                handleCopyValue(
                  createdCredentials?.clientSecret,
                  "Client secret copied"
                )
              }
              aria-label="Copy client secret"
              disabled={!createdCredentials?.clientSecret}
            >
              <FiCopy size={18} />
            </button>
          </div>
        </Modal.Body>
        <Modal.Footer className="api-token-modal-footer">
          <Button
            className="api-token-primary-btn"
            type="button"
            onClick={() => setShowCredentialsModal(false)}
          >
            Done
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ApiTokenManagement;
